<script>
import RexTableCompV2 from '@/rex-components/rex-TableCompV2.vue';
import LogSavedSearchTableActions from '@/components/Log/LogSavedSearchTableActions.vue';
import RexTooltipV3 from '@/rex-components/RexTooltipV3.vue';
import { savedSearchHeaders, sortOrder } from '@/components/Log/log.types';
import { LOGService } from '@/services/api/log.service';
import { LOG_SAVED_SEARCH_TABLE_LIMIT } from '@/components/Log/log.constants';
import { getRandomUniqueId } from '@/utils/helper-function';
import { mapGetters, mapMutations, mapState } from 'vuex';
import { TABLE_QUERY_TYPE } from '@/components/Log/log.constants';
import { notificationError } from '@/utils/notification';

export default {
  components: { RexTableCompV2, LogSavedSearchTableActions, RexTooltipV3 },
  computed: {
    ...mapState('globalState/user', ['teamID']),
    ...mapGetters('globalState/settings', ['getDurationTime']),
    ...mapState('logState/logCommonState', ['reloadSavedSearchTable']),
    ...mapState('globalState/app', ['isFormOpened']),
    ...mapGetters('logState/logCommonState', ['getCapabilityType']),
    dispInfiniteFooter() {
      return this.stopInfiniteScroll && this.items?.length > 18;
    },
    dispScrollFooter() {
      return this.items?.length > 18;
    },
    headers() {
      return savedSearchHeaders();
    },
  },
  data() {
    return {
      items: [],
      isError: false,
      currentPage: 1,
      stopInfiniteScroll: false,
      loading: false,
    };
  },
  methods: {
    ...mapMutations('logState/logCommonState', ['setReloadSavedSearchTable']),
    formatTableResp(list) {
      return list.map((item) => ({
        _id: `${getRandomUniqueId()}`,
        ...item,
      }));
    },
    async getSavedSearchTable() {
      try {
        this.isError = false;
        this.loading = true;
        const { data } = await LOGService.getSavedSearchTableData({
          team_id: this.teamID,
          limit: this.pageSize,
          offset: (this.currentPage - 1) * this.pageSize,
          order: sortOrder.ASC,
          capability: this.getCapabilityType,
        });
        this.items.push(...this.formatTableResp(data));
        this.items =
          this.currentPage === 1
            ? this.formatTableResp(data)
            : [...this.items, ...this.formatTableResp(data)];
        if (data?.length < this.pageSize) {
          this.stopInfiniteScroll = true;
        }
        this.loading = false;
        this.setReloadSavedSearchTable(false);
      } catch (error) {
        this.setReloadSavedSearchTable(false);
        this.isError = true;
        console.log(error);
        this.loading = false;
        this.stopInfiniteScroll = true;
        error?.message?.toLowerCase()?.includes('timeout') &&
          notificationError(this.$t('log.timeoutErrMsg'));
      }
    },
    resetTableData() {
      this.currentPage = 1;
      this.items = [];
      this.stopInfiniteScroll = false;
      this.loading = false;
    },
    formatQueryType(type) {
      return type === TABLE_QUERY_TYPE ? 'Table' : 'Metric';
    },
  },
  created() {
    this.pageSize = LOG_SAVED_SEARCH_TABLE_LIMIT;
    this.getSavedSearchTable();
  },
  watch: {
    getDurationTime() {
      if (!this.isFormOpened) {
        this.resetTableData();
        this.getSavedSearchTable();
      }
    },
    reloadSavedSearchTable(newVal) {
      if (newVal) {
        this.resetTableData();
        this.getSavedSearchTable();
      }
    },
  },
};
</script>

<template>
  <div>
    <RexTableCompV2
      :items="items"
      :headers="headers"
      :loading="loading"
      :striped="true"
      :enableInfiniteScroll="true"
      :enableScrollFooter="dispScrollFooter"
      @fetchNextPageData="
        () => {
          if (items.length > 0 && !stopInfiniteScroll) {
            currentPage++;
            getSavedSearchTable();
          }
        }
      "
    >
      <template #body-actions="{ item }">
        <LogSavedSearchTableActions :rowDetails="item" />
      </template>

      <template #body-savedQueryType="{ item }">
        <span>{{ formatQueryType(item.savedQueryType) }}</span>
      </template>

      <template #body-metric_name="{ item }">
        <RexTooltipV3
          :title="item.metric_name"
          class="saved-table-break-content"
        >
          <div class="saved-table-break-text">
            {{ item.metric_name }}
          </div>
        </RexTooltipV3>
      </template>

      <template #body-query="{ item }">
        <RexTooltipV3 :title="item.query" class="saved-table-break-content">
          <div class="saved-table-break-text">
            {{ item.query }}
          </div>
        </RexTooltipV3>
      </template>

      <template v-if="dispInfiniteFooter" #infinite-footer>
        <div class="log-saved-infi-footer">
          <div v-if="isError">
            {{ $t('log.errorWhileFetchingData') }}
            <button
              class="refresh-btn"
              @click="
                () => {
                  resetTableData();
                  getSavedSearchTable();
                }
              "
            >
              <BaseIcon
                name="refresh"
                height="0.8em"
                width="0.8em"
                class="log-refresh-icon"
              />
            </button>
          </div>
          <div v-else>
            {{ $t('log.availableDataLoadedCompletely') }}
          </div>
        </div>
      </template>
    </RexTableCompV2>
  </div>
</template>

<style lang="scss" scoped>
.saved-table-break-content {
  overflow: hidden;
  white-space: nowrap;
}
.saved-table-break-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.log-saved-infi-footer {
  @include flex-center;
  color: $font-tertiary;
}
.refresh-btn {
  border: none;
  background-color: inherit;
}
.log-refresh-icon {
  @include svg-fill-pointer($color-brand-accent);
}
:deep(.sort-asc) {
  margin-bottom: -1.1em;
}
:deep(.body-td) {
  word-break: break-all;
}
</style>
